import { useEffect, useState, useCallback, lazy, Suspense, useRef } from 'react';
import { Routes, Route } from 'react-router-dom';
import { ErrorBoundary } from '@components/ErrorBoundary';
import { HazardNotificationProvider } from './contexts/HazardNotificationContext';
import { telegram } from '@services/telegram';
import { loadGoogleMaps } from '@services/googleMaps';
import { subscriptionService } from '@services/subscriptionService';
import crewService from '@services/crewService';
import { useAppStore } from '@stores/appStore';
import { Box, Button, Typography, Alert, CircularProgress, TextField, Chip } from '@mui/material';
import { installErrorLogger, logError } from '@services/errorLogger';
import {
  captureSignCampaignFromUrl,
  installSignCampaignFirstInteractionTracker,
  recordSignCampaignEvent,
} from '@services/signCampaignAnalytics';
import { getTranslation, useTranslation } from './i18n/translations';
import { RaceRouteSplashGate } from './components/RaceLaunchSplash';
import { getRuntimeMode, getRuntimeSupportProfile } from '@services/runtimeCapabilities';
import { AppFeedbackHost, showToast } from '@services/appFeedback';

// Install client-side error capture as early as possible so we record
// initialization-time failures (Google Maps auth, module load errors, etc.)
installErrorLogger();

// Clear the ErrorBoundary's stale-chunk reload flag on a successful app load.
// Without this, a second stale-chunk crash within the same session would skip
// the auto-reload path and show the user the error screen unnecessarily.
try { sessionStorage.removeItem('stale_chunk_reload_attempted'); } catch { /* noop */ }

// Store original fetch globally for offline simulation
// Guard: some Android WebViews temporarily shadow window.fetch with undefined
const originalFetch = (window.fetch || fetch).bind(window);
(window as any).__originalFetch = originalFetch;

const VoiceNavigation = lazy(() => import('@components/VoiceNavigation'));
const LandingPage = lazy(() =>
  import('@components/VamosLandingPage').then((module) => ({ default: module.VamosLandingPage })),
);
const PricingSection = lazy(() => import('@components/PricingSection'));
const loadRaceApp = () => import('../race/RaceApp').then((module) => ({ default: module.RaceApp }));
const RaceApp = lazy(loadRaceApp);

const SUBSCRIPTION_GRACE_NOTICE_STORAGE_KEY = 'ziawellnav_subscription_grace_notice_day';
const FREE_MODE_COUNTDOWN_TOP_CSS = 'calc(var(--tg-content-safe-top, env(safe-area-inset-top, 0px)) + 74px)';
const FREE_MODE_DEMO_NOTICE_TOP_CSS = 'calc(var(--tg-content-safe-top, env(safe-area-inset-top, 0px)) + 116px)';
const FREE_MODE_COUNTDOWN_MOBILE_BOTTOM_CSS = 'calc(env(safe-area-inset-bottom, 0px) + 112px)';
const FREE_MODE_COUNTDOWN_MOBILE_SIDE_GUTTER_CSS = 'calc(env(safe-area-inset-left, 0px) + 12px)';
const FREE_MODE_COUNTDOWN_MOBILE_RIGHT_GUTTER_CSS = 'calc(env(safe-area-inset-right, 0px) + 12px)';
const FREE_MODE_DEMO_NOTICE_IOS_TOP_CSS = 'calc(50dvh + 138px)';

function shouldShowSubscriptionGraceNotice(): boolean {
  try {
    const today = new Date().toISOString().slice(0, 10);
    if (localStorage.getItem(SUBSCRIPTION_GRACE_NOTICE_STORAGE_KEY) === today) return false;
    localStorage.setItem(SUBSCRIPTION_GRACE_NOTICE_STORAGE_KEY, today);
    return true;
  } catch {
    return true;
  }
}

function FullScreenLoader({ label }: { label: string }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        backgroundColor: '#0f172a',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      <CircularProgress sx={{ color: '#06b6d4' }} />
      <Typography sx={{ color: '#94a3b8', fontWeight: 600 }}>{label}</Typography>
    </Box>
  );
}

function InlineLoader({ label }: { label: string }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, py: 2 }}>
      <CircularProgress size={18} sx={{ color: '#06b6d4' }} />
      <Typography variant="caption" sx={{ color: '#94a3b8' }}>{label}</Typography>
    </Box>
  );
}

const FREE_MODE_STORAGE_KEY = 'zia_free_mode_expires_at';
const FREE_MODE_DURATION_MS = 30 * 60 * 1000;
const GLOBE3D_OPEN_INTENT_KEY = 'zia_pending_open_globe3d';

function getFreeModeRemainingMs(): number {
  try {
    const raw = localStorage.getItem(FREE_MODE_STORAGE_KEY);
    const expiresAt = raw ? Number(raw) : 0;
    if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
      localStorage.removeItem(FREE_MODE_STORAGE_KEY);
      return 0;
    }
    return expiresAt - Date.now();
  } catch {
    return 0;
  }
}

function startLocalFreeMode(): number {
  const expiresAt = Date.now() + FREE_MODE_DURATION_MS;
  try {
    localStorage.setItem(FREE_MODE_STORAGE_KEY, String(expiresAt));
  } catch {
    // If storage is blocked, still allow this render cycle to start the demo.
  }
  return FREE_MODE_DURATION_MS;
}

function shouldStartFreeModeFromUrl(params: URLSearchParams): boolean {
  const freeMode = (params.get('freeMode') || params.get('free') || '').toLowerCase();
  const mode = (params.get('mode') || '').toLowerCase();
  return ['1', 'true', 'yes', 'start'].includes(freeMode) || mode === 'free';
}

function getLanguageFromUrl(params: URLSearchParams): 'en' | 'es' | null {
  const explicit = (params.get('lang') || params.get('language') || '').toLowerCase();
  const source = (params.get('src') || '').toLowerCase();
  if (explicit === 'es' || explicit === 'spanish' || source === 'spanish' || source === 'es') return 'es';
  if (explicit === 'en' || explicit === 'english' || source === 'english' || source === 'en') return 'en';
  return null;
}

function PublicInspectionViewer({ inspectionId, onClose }: { inspectionId: string; onClose: () => void }) {
  const { t, language } = useTranslation();
  const [html, setHtml] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true); setError(null); setHtml(null);
      try {
        const res = await fetch(`/api/inspections/${encodeURIComponent(inspectionId)}/pdf?shared=1&view=1`);
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || getTranslation(language, 'inspectionLoadFailedHttp', String(res.status)));
        }
        const text = await res.text();
        if (!cancelled) setHtml(text);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : getTranslation(language, 'inspectionLoadFailed'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [inspectionId, language]);

  return (
    <div style={{ minHeight: '100vh', background: '#0f172a', color: '#e2e8f0', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.25, bgcolor: '#1e293b', borderBottom: '1px solid rgba(148,163,184,0.25)' }}>
        <Typography sx={{ fontWeight: 800, color: '#fff' }}>{t('inspectionReportTitle')}</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" size="small" component="a" href={`/api/inspections/${inspectionId}/pdf?shared=1`} target="_blank" rel="noopener noreferrer" sx={{ textTransform: 'none', fontWeight: 700, borderColor: 'rgba(245,158,11,0.6)', color: '#f59e0b' }}>
            {t('downloadPdfBtn')}
          </Button>
          <Button variant="outlined" size="small" onClick={onClose} sx={{ textTransform: 'none', fontWeight: 800, borderColor: 'rgba(255,255,255,0.35)', color: '#fff' }}>
            {t('close')}
          </Button>
        </Box>
      </Box>
      {loading && (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <CircularProgress sx={{ color: '#f59e0b' }} />
          <Typography sx={{ color: '#94a3b8' }}>{t('loadingInspection')}</Typography>
        </Box>
      )}
      {!loading && error && (
        <Box sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, justifyContent: 'center', maxWidth: 720, width: '100%', mx: 'auto' }}>
          <Alert severity="error">{error}</Alert>
          <Button variant="contained" onClick={onClose} sx={{ textTransform: 'none', fontWeight: 800, alignSelf: 'flex-start' }}>{t('back')}</Button>
        </Box>
      )}
      {!loading && !error && html && (
        <iframe srcDoc={html} style={{ width: '100%', flex: 1, border: 'none', background: '#fff' }} title={t('inspectionReportTitle')} />
      )}
    </div>
  );
}

function PublicJsaPdfViewer({ jsaId, onClose }: { jsaId: string; onClose: () => void }) {
  const { t, language } = useTranslation();
  const [html, setHtml] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setError(null);
      setHtml(null);
      try {
        const res = await fetch(`/api/jsa/${encodeURIComponent(jsaId)}/pdf?shared=1`, {
          headers: { 'Accept': 'text/html' },
        });
        if (!res.ok) {
          const msg = res.status === 404
            ? getTranslation(language, 'jsaNotFound')
            : getTranslation(language, 'jsaLoadFailedHttp', String(res.status));
          throw new Error(msg);
        }
        const text = await res.text();
        if (!cancelled) setHtml(text);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : getTranslation(language, 'jsaLoadFailed'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => { cancelled = true; };
  }, [jsaId, language]);

  return (
    <div style={{ minHeight: '100vh', background: '#0f172a', color: '#e2e8f0', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.25, bgcolor: '#1e293b', borderBottom: '1px solid rgba(148,163,184,0.25)' }}>
        <Typography sx={{ fontWeight: 800, color: '#fff' }}>{t('jsaReportTitle')}</Typography>
        <Button variant="outlined" size="small" onClick={onClose} sx={{ textTransform: 'none', fontWeight: 800, borderColor: 'rgba(255,255,255,0.35)', color: '#fff' }}>
          {t('close')}
        </Button>
      </Box>

      {loading && (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <CircularProgress sx={{ color: '#60a5fa' }} />
          <Typography sx={{ color: '#94a3b8' }}>{t('loadingJsa')}</Typography>
        </Box>
      )}

      {!loading && error && (
        <Box sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, justifyContent: 'center', maxWidth: 720, width: '100%', mx: 'auto' }}>
          <Alert severity="error">{error}</Alert>
          <Button variant="contained" onClick={onClose} sx={{ textTransform: 'none', fontWeight: 800, alignSelf: 'flex-start' }}>
            {t('back')}
          </Button>
        </Box>
      )}

      {!loading && !error && html && (
        <iframe
          srcDoc={html}
          style={{ width: '100%', flex: 1, border: 'none', background: '#fff' }}
          title={t('jsaReportTitle')}
        />
      )}
    </div>
  );
}

function App() {
  const { t, language } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [subscriptionChecked, setSubscriptionChecked] = useState(false);
  const [subscriptionAllowed, setSubscriptionAllowed] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState<string | null>(null);
  const [subscriptionGraceNotice, setSubscriptionGraceNotice] = useState<string | null>(null);
  const [checkingSubscription, setCheckingSubscription] = useState(false);
  const [linkEmail, setLinkEmail] = useState('');
  const [linking, setLinking] = useState(false);
  const [linkingTelegram, setLinkingTelegram] = useState(false);
  const [linkMsg, setLinkMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showPricing, setShowPricing] = useState(false);
  const [starsProcessing, setStarsProcessing] = useState(false);
  const [starsMessage, setStarsMessage] = useState<{ type: 'info' | 'success' | 'error'; text: string } | null>(null);
  const [starsPaywall, setStarsPaywall] = useState<NonNullable<Awaited<ReturnType<typeof subscriptionService.getStatus>>['starsPaywall']> | null>(null);
  const [freeModeRemainingMs, setFreeModeRemainingMs] = useState(() => getFreeModeRemainingMs());
  const [freeModeDemoNoticeVisible, setFreeModeDemoNoticeVisible] = useState(false);
  const subscriptionAbortRef = useRef<AbortController | null>(null);
  const latestSubscriptionCheckRef = useRef(0);
  const subscriptionNextRetryAtRef = useRef(0);
  const simulateOffline = useAppStore((state) => state.simulateOffline);
  const sharedJsaId = useAppStore((state) => state.sharedJsaId);

  const setSharedJsaId = useAppStore((state) => state.setSharedJsaId);
  const sharedInspectionId = useAppStore((state) => state.sharedInspectionId);
  const setSharedInspectionId = useAppStore((state) => state.setSharedInspectionId);

  const freeModeActive = freeModeRemainingMs > 0;
  const freeModeMinutesRemaining = Math.max(1, Math.ceil(freeModeRemainingMs / 60000));
  const runtimeMode = getRuntimeMode();
  const runtimeSupportProfile = getRuntimeSupportProfile(runtimeMode);
  const isBrowserFreeDemo = freeModeActive && !subscriptionAllowed && runtimeMode !== 'telegram-mini-app';
  const accessAllowed = subscriptionAllowed || freeModeActive;

  const handleStartFreeMode = useCallback((dashboardTarget?: 'safety' | 'training' | 'fleet') => {
    if (dashboardTarget) {
      try { localStorage.setItem('zia_pending_company_dashboard_tab', dashboardTarget); } catch { /* ignore */ }
    }
    subscriptionAbortRef.current?.abort();
    setCheckingSubscription(false);
    setSubscriptionError(null);
    setFreeModeRemainingMs(startLocalFreeMode());
    setFreeModeDemoNoticeVisible(true);
  }, []);

  useEffect(() => {
    const refreshFreeMode = () => {
      setFreeModeRemainingMs(getFreeModeRemainingMs());
    };
    refreshFreeMode();
    const intervalId = window.setInterval(refreshFreeMode, 1000);
    return () => window.clearInterval(intervalId);
  }, []);
  
  // Install/update fetch interceptor when simulateOffline changes
  useEffect(() => {
    const origFetch = (window as any).__originalFetch || originalFetch;
    
    if (simulateOffline) {
      // Override fetch to simulate network failure
      window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
        const url = typeof input === 'string' ? input : input instanceof URL ? input.href : (input as Request).url;
        
        // Allow some essential requests through
        if (url.includes('chrome-extension://') || url.startsWith('blob:') || url.startsWith('data:')) {
          return origFetch(input, init);
        }
        
        console.log('🚫 [OFFLINE SIMULATION] Blocked request:', url);
        
        // Simulate network error with a slight delay to feel realistic
        await new Promise(r => setTimeout(r, 100 + Math.random() * 200));
        throw new TypeError('Failed to fetch (simulated offline mode)');
      };
      
      console.log('📴 [OFFLINE SIMULATION] Enabled - all API requests will fail');
    } else {
      // Restore original fetch
      window.fetch = origFetch;
      console.log('📶 [OFFLINE SIMULATION] Disabled - normal network restored');
    }
  }, [simulateOffline]);

  // Stable auth handler that can be used as global fallback
  const handleTelegramAuth = useCallback(async (user: any) => {
    console.log('handleTelegramAuth called with:', JSON.stringify(user));

    // Accept any valid user object from Telegram
    if (user) {
      const userId = user.id || user.user?.id;
      const firstName = user.first_name || user.user?.first_name || 'User';

      if (userId) {
        console.log('Telegram login successful:', firstName, 'ID:', userId);
        // Persist auth so it survives page refreshes within the same browser session
        sessionStorage.setItem('tg_auth_user', JSON.stringify(user));
        // Persist user ID to localStorage so telegram.getUserId() returns it
        // on API calls (Login Widget doesn't populate initDataUnsafe)
        localStorage.setItem('telegram_user_id', String(userId));

        // Auto-link Stripe purchase if user came from checkout page
        const pendingEmail = sessionStorage.getItem('pendingLinkEmail');
        if (pendingEmail) {
          sessionStorage.removeItem('pendingLinkEmail');
          try {
            const result = await subscriptionService.linkPurchaseByEmail(pendingEmail.trim());
            if (result.success) subscriptionService.clearCache();
          } catch { /* silent — subscription gate handles fallback */ }
        }

        setIsAuthenticated(true);
        return;
      }
    }

    console.error('Invalid user data from Telegram:', user);
    showToast({ message: 'Authentication failed. Please try again.', severity: 'error' });
  }, []);

  useEffect(() => {
    // Set up global fallback callback for Telegram Login Widget
    // This catches auth even if component callbacks fail
    (window as any).TelegramLoginCallback = handleTelegramAuth;
    
    // Initialize Telegram WebApp (wrap in try/catch — older Android Telegram clients
    // may define Telegram.WebApp but have unimplemented methods that throw)
    try {
      telegram.initialize();
    } catch (err) {
      console.warn('[App] telegram.initialize() failed (older Telegram client):', err);
    }
    
    // Load Google Maps API
    loadGoogleMaps().catch(err => {
      console.warn('Google Maps failed to load:', err);
    });

    // Check for shared route URL parameter (?route=123)
    const urlParams = new URLSearchParams(window.location.search);
    const routeParam = urlParams.get('route');
    const licenseParam = urlParams.get('license');
    const checkoutSuccess = urlParams.get('checkout') === 'success';
    const openParam = (urlParams.get('open') || '').toLowerCase();
    const startFreeMode = shouldStartFreeModeFromUrl(urlParams);
    const qrLanguage = getLanguageFromUrl(urlParams);
    const signCampaignAttribution = captureSignCampaignFromUrl(urlParams);
    if (openParam === 'globe3d') {
      try { window.sessionStorage.setItem(GLOBE3D_OPEN_INTENT_KEY, '1'); } catch { /* ignore */ }
    }
    if (signCampaignAttribution) {
      recordSignCampaignEvent('scan_landed');
      installSignCampaignFirstInteractionTracker();
    }
    if (qrLanguage) {
      useAppStore.getState().setLanguage(qrLanguage);
    }
    if (startFreeMode) {
      setFreeModeRemainingMs(startLocalFreeMode());
      setFreeModeDemoNoticeVisible(true);
      recordSignCampaignEvent('free_mode_started');
    }
    if (checkoutSuccess) {
      sessionStorage.setItem('checkoutPending', '1');
      const sessionId = urlParams.get('session_id');
      if (sessionId) sessionStorage.setItem('checkoutSessionId', sessionId);
    }
    const startParam = telegram.getStartParam();
    const companyBotModule = urlParams.get('companyBotModule') || '';
    if (companyBotModule) {
      const dashboardTabByModule: Record<string, string> = {
        safety: 'safety',
        crews: 'crews',
        meetings: 'meetings',
        jobMasters: 'jobMasters',
        job_masters: 'jobMasters',
        fleet: 'fleet',
        records: 'records',
        certs: 'certs',
        training: 'training',
        policies: 'policies',
      };
      const dashboardTab = dashboardTabByModule[companyBotModule];
      if (dashboardTab) {
        localStorage.setItem('zia_pending_company_dashboard_tab', dashboardTab);
      }
    }
    if (routeParam) {
      const routeId = parseInt(routeParam, 10);
      if (!isNaN(routeId)) {
        console.log('Shared route detected:', routeId);
        useAppStore.getState().setSharedRoutePhotoId(routeId);
      }
    }

    // Check for shared facility URL parameter (?facility=xxx)
    const facilityParam = urlParams.get('facility');
    if (facilityParam) {
      console.log('Shared facility detected:', facilityParam);
      useAppStore.getState().setSharedFacilityId(facilityParam);
    }

    // Check for shared inspection URL parameter (?inspection=xxx)
    const inspectionParam = urlParams.get('inspection');
    if (inspectionParam) {
      console.log('Shared inspection detected:', inspectionParam);
      useAppStore.getState().setSharedInspectionId(inspectionParam);
    }

    // Clean the URL if any params were present
    if (licenseParam) {
      localStorage.setItem('pendingLicenseKey', licenseParam);
    }

    // Process start parameter (Telegram start_param or ?startapp=xxx) regardless of auth
    if (startParam && startParam.startsWith('nearby_')) {
      const parts = startParam.split('_');
      if (parts.length === 3) {
        const lat = parseFloat(parts[1]);
        const lng = parseFloat(parts[2]);
        useAppStore.getState().setNearbyLocation({ lat, lng });
      }
    }
    if (startParam && startParam.startsWith('inspection_')) {
      const inspId = startParam.replace('inspection_', '');
      if (inspId) {
        console.log('Shared inspection from start param:', inspId);
        useAppStore.getState().setSharedInspectionId(inspId);
      }
    }
    if (startParam && startParam.startsWith('jsa_prompt_')) {
      const promptId = startParam.replace('jsa_prompt_', '');
      if (promptId) {
        console.log('JSA geofence prompt from start param:', promptId);
        useAppStore.getState().setPendingJsaPromptId(promptId);
      }
    }
    if (startParam && startParam.startsWith('jsa_') && !startParam.startsWith('jsa_prompt_')) {
      const jsaId = startParam.replace('jsa_', '');
      if (jsaId) {
        console.log('Shared JSA from start param:', jsaId);
        useAppStore.getState().setSharedJsaId(jsaId);
      }
    }
    if (startParam && startParam.startsWith('track_')) {
      const trackId = startParam.replace('track_', '');
      if (trackId) {
        console.log('Shared race track from start param:', trackId);
        useAppStore.getState().setPendingTrackId(trackId);
      }
    }
    const trackQueryParam = new URLSearchParams(window.location.search).get('track');
    if (trackQueryParam) {
      console.log('Shared race track from URL param:', trackQueryParam);
      useAppStore.getState().setPendingTrackId(trackQueryParam);
    }
    if (startParam && startParam.startsWith('meeting_')) {
      const meetingId = startParam.replace('meeting_', '');
      if (meetingId) {
        console.log('Company meeting from start param:', meetingId);
        useAppStore.getState().setPendingMeetingId(meetingId);
      }
    }
    if (startParam && startParam.startsWith('join_')) {
      const inviteCode = startParam.replace('join_', '');
      if (inviteCode) {
        console.log('Crew invite from start param:', inviteCode);
        localStorage.setItem('pendingCrewInviteCode', inviteCode);
      }
    }
    if (startParam && startParam.startsWith('jsamap_')) {
      const parts = startParam.split('_'); // ['jsamap', '<lat>', '<lng>']
      if (parts.length === 3) {
        const lat = parseFloat(parts[1]);
        const lng = parseFloat(parts[2]);
        if (!isNaN(lat) && !isNaN(lng)) {
          window.__navigateToCoords = { lat, lng, name: 'JSA Muster Point' };
          useAppStore.getState().setShowFavoritesMap(true);
        }
      }
    }
    if (startParam && startParam.startsWith('hazard_')) {
      const parts = startParam.split('_'); // ['hazard', '<lat>', '<lng>']
      if (parts.length === 3) {
        const lat = parseFloat(parts[1]);
        const lng = parseFloat(parts[2]);
        if (!isNaN(lat) && !isNaN(lng)) {
          window.__navigateToCoords = { lat, lng, name: 'Reported Hazard' };
          // Persist across auth in case the user lands on the subscription
          // gate first — VoiceNavigation rehydrates from localStorage on mount.
          try { localStorage.setItem('zia_pending_hazard_map', JSON.stringify({ lat, lng })); } catch { /* ignore */ }
          useAppStore.getState().setShowFavoritesMap(true);
        }
      }
    }
    // Telegram bot "View in AR" deep-link from a hazard alert.
    if (startParam && startParam.startsWith('arhazard_')) {
      const parts = startParam.split('_'); // ['arhazard', '<lat>', '<lng>']
      if (parts.length === 3) {
        const lat = parseFloat(parts[1]);
        const lng = parseFloat(parts[2]);
        if (!isNaN(lat) && !isNaN(lng)) {
          // Stash the target — VoiceNavigation reads this on mount + after
          // auth, opens AR Live View, and starts the laser-route navigation.
          try { localStorage.setItem('zia_pending_hazard_ar', JSON.stringify({ lat, lng })); } catch { /* ignore */ }
          (window as any).__pendingArHazard = { lat, lng, name: 'Reported Hazard' };
        }
      }
    }
    if (startParam && startParam.startsWith('liveshare_')) {
      // Pending live location share invitation — auto-accept on AR Live View open
      const sessionId = startParam.replace('liveshare_', '');
      if (sessionId) {
        console.log('Live share invitation from start param:', sessionId);
        (window as any).__pendingLiveShareId = sessionId;
        // Auto-accept immediately so the card shows up next time AR Live View opens
        (async () => {
          try {
            const { liveShareService } = await import('./services/liveShareService');
            await liveShareService.acceptShare(sessionId);
          } catch (e) {
            console.warn('Failed to auto-accept live share:', e);
          }
        })();
      }
    }

    // Check if already authenticated via Telegram WebApp (in-app context)
    const user = telegram.getUser();
    if (user) {
      setIsAuthenticated(true);
    } else {
      // Restore browser session auth (Telegram Login Widget — survives refresh, clears on tab close)
      const savedAuth = sessionStorage.getItem('tg_auth_user');
      if (savedAuth) {
        try {
          const savedUser = JSON.parse(savedAuth);
          const savedId = savedUser?.id || savedUser?.user?.id;
          if (savedId) {
            console.log('[App] Restored Telegram Login Widget session for user', savedId);
            localStorage.setItem('telegram_user_id', String(savedId));
            setIsAuthenticated(true);
          }
        } catch {
          sessionStorage.removeItem('tg_auth_user');
        }
      }
    }

    if (routeParam || facilityParam || licenseParam || checkoutSuccess || inspectionParam || startParam || startFreeMode || signCampaignAttribution || openParam) {
      window.history.replaceState({}, '', window.location.pathname);
    }
    
    // Need Telegram Login Widget (unless viewing shared content)
    setIsLoading(false);
    
    return () => {
      delete (window as any).TelegramLoginCallback;
    };
  }, [handleTelegramAuth]);

  const runSubscriptionCheck = useCallback(async (
    reason: 'initial-auth' | 'checkout-retry' | 'manual-retry' | 'link-purchase' | 'online' | 'stars-payment'
  ): Promise<{ allowed: boolean; verificationError: boolean }> => {
    if (!isAuthenticated) {
      return { allowed: false, verificationError: true };
    }

    const allowFromGrace = () => {
      const graceStatus = subscriptionService.getCachedGraceStatus();
      if (!graceStatus) return false;
      setSubscriptionAllowed(true);
      setSubscriptionError(null);
      if (shouldShowSubscriptionGraceNotice()) {
        setSubscriptionGraceNotice('Subscription verified from recent active status. We will refresh it quietly when the connection is stable.');
      }
      return true;
    };

    const canBypassBackoff = reason === 'manual-retry' || reason === 'checkout-retry' || reason === 'link-purchase';
    if (!canBypassBackoff && subscriptionNextRetryAtRef.current > Date.now()) {
      if (allowFromGrace()) return { allowed: true, verificationError: false };
      return { allowed: false, verificationError: true };
    }

    localStorage.removeItem('sub_gate_dismissed_at');
    const checkId = ++latestSubscriptionCheckRef.current;
    subscriptionAbortRef.current?.abort();
    const controller = new AbortController();
    subscriptionAbortRef.current = controller;
    setCheckingSubscription(true);
    setSubscriptionError(null);

    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const finish = (result: { allowed: boolean; verificationError: boolean }) => {
      if (checkId === latestSubscriptionCheckRef.current) {
        setSubscriptionChecked(true);
        setCheckingSubscription(false);
      }
      if (subscriptionAbortRef.current === controller) {
        subscriptionAbortRef.current = null;
      }
      if (timeoutId) clearTimeout(timeoutId);
      return result;
    };

    try {
      timeoutId = setTimeout(() => controller.abort(), 8000);
      const status = await subscriptionService.getStatus(true, controller.signal);
      if (checkId !== latestSubscriptionCheckRef.current) {
        return finish({ allowed: false, verificationError: true });
      }

      if (status.status === 'error') {
        if (allowFromGrace()) {
          return finish({ allowed: true, verificationError: false });
        }
        subscriptionNextRetryAtRef.current = Date.now() + 5 * 60 * 1000;
        setSubscriptionAllowed(false);
        setSubscriptionError(getTranslation(language, 'subscriptionVerifyFailed'));
        logError('subscription.verify', status.lastError || 'Subscription verification failed', {
          level: 'warn',
          extra: {
            reason,
            source: status.source,
            httpStatus: status.httpStatus,
            checkedAt: status.checkedAt,
          },
        });
        return finish({ allowed: false, verificationError: true });
      }

      let allowed = status.isSubscribed || status.status === 'trialing';
      setStarsPaywall(status.starsPaywall || null);
      subscriptionNextRetryAtRef.current = 0;
      if (allowed && status.accessSource === 'soft_launch') {
        setSubscriptionGraceNotice('Telegram Stars is in soft launch. Access is open while we finish live payment validation; Stripe and business seats still work normally.');
      } else if (allowed) {
        setSubscriptionGraceNotice(null);
      }

      // Returning from Stripe checkout — webhook may not have fired yet, retry up to 4×
      if (!allowed && sessionStorage.getItem('checkoutPending')) {
        sessionStorage.removeItem('checkoutPending');
        for (let attempt = 0; attempt < 4; attempt++) {
          await new Promise(r => setTimeout(r, 4000));
          const retry = await subscriptionService.getStatus(true);
          if (retry.status === 'error') {
            if (allowFromGrace()) {
              return finish({ allowed: true, verificationError: false });
            }
            subscriptionNextRetryAtRef.current = Date.now() + 5 * 60 * 1000;
            setSubscriptionAllowed(false);
            setSubscriptionError(getTranslation(language, 'subscriptionVerifyFailed'));
            logError('subscription.verify', retry.lastError || 'Checkout follow-up verification failed', {
              level: 'warn',
              extra: {
                reason: 'checkout-retry',
                attempt: attempt + 1,
                source: retry.source,
                httpStatus: retry.httpStatus,
                checkedAt: retry.checkedAt,
              },
            });
            return finish({ allowed: false, verificationError: true });
          }
          if (retry.isSubscribed || retry.status === 'trialing') {
            allowed = true;
            break;
          }
        }
      }

      setSubscriptionAllowed(allowed);
      setSubscriptionError(allowed ? null : getTranslation(language, 'subscriptionRequiredAccess'));
      return finish({ allowed, verificationError: false });
    } catch (err: any) {
      if (checkId !== latestSubscriptionCheckRef.current) {
        return finish({ allowed: false, verificationError: true });
      }
      if (allowFromGrace()) {
        return finish({ allowed: true, verificationError: false });
      }
      subscriptionNextRetryAtRef.current = Date.now() + 5 * 60 * 1000;
      console.warn('[App] Subscription check failed:', err?.message || err);
      setSubscriptionAllowed(false);
      setSubscriptionError(getTranslation(language, 'subscriptionVerifyFailed'));
      logError('subscription.verify', err?.message || 'Subscription verification request failed', {
        level: 'warn',
        extra: { reason },
      });
      return finish({ allowed: false, verificationError: true });
    }
  }, [isAuthenticated, language]);

  const handleStarsAccessPayment = useCallback(async (productType: 'individual_monthly' | 'individual_yearly' = 'individual_monthly') => {
    setStarsProcessing(true);
    setStarsMessage(null);
    try {
      const invoice = await subscriptionService.createStarsInvoice({ productType });
      const tg = (window as any).Telegram?.WebApp;

      const verifyAccess = async () => {
        setStarsMessage({ type: 'info', text: 'Payment received. Unlocking access...' });
        const status = await subscriptionService.pollForActiveAccess();
        if (status.isSubscribed || status.status === 'trialing') {
          setSubscriptionAllowed(true);
          setSubscriptionError(null);
          setStarsPaywall(null);
          setStarsMessage({ type: 'success', text: 'Access unlocked. Welcome to ZiaWellNav.' });
          return;
        }
        await runSubscriptionCheck('stars-payment');
        setStarsMessage({ type: 'error', text: 'Payment is still processing. Tap Try again in a moment.' });
      };

      if (tg?.openInvoice) {
        tg.openInvoice(invoice.invoiceLink, (status: string) => {
          if (status === 'paid') {
            void verifyAccess().finally(() => setStarsProcessing(false));
          } else if (status === 'cancelled') {
            setStarsMessage({ type: 'info', text: 'Stars payment was cancelled.' });
            setStarsProcessing(false);
          } else {
            setStarsMessage({ type: 'error', text: 'Stars payment was not completed.' });
            setStarsProcessing(false);
          }
        });
      } else {
        window.open(invoice.invoiceLink, '_blank', 'noopener,noreferrer');
        setStarsMessage({ type: 'info', text: 'Invoice opened. Return here after paying and tap Try again.' });
        setStarsProcessing(false);
      }
    } catch (error) {
      setStarsMessage({ type: 'error', text: error instanceof Error ? error.message : 'Failed to start Telegram Stars payment.' });
      setStarsProcessing(false);
    }
  }, [runSubscriptionCheck]);

  // Subscription gate: check after authentication.
  useEffect(() => {
    void runSubscriptionCheck('initial-auth');
  }, [runSubscriptionCheck]);

  useEffect(() => () => {
    subscriptionAbortRef.current?.abort();
  }, []);

  useEffect(() => {
    if (!isAuthenticated || subscriptionAllowed) return;

    const handleOnline = () => { void runSubscriptionCheck('online'); };

    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [isAuthenticated, subscriptionAllowed, runSubscriptionCheck]);

  // Auto-claim pending license (set via ?license=KEY) once authenticated
  useEffect(() => {
    const maybeClaim = async () => {
      if (!isAuthenticated) return;
      const key = localStorage.getItem('pendingLicenseKey');
      if (!key) return;
      try {
        const res = await subscriptionService.claimLicense(key);
        if (res.success) {
          console.log('License claimed from URL param');
          localStorage.removeItem('pendingLicenseKey');
        } else {
          console.warn('License claim failed:', res.error);
        }
      } catch (err) {
        console.warn('License claim error:', err);
      }
    };
    maybeClaim();
  }, [isAuthenticated]);

  // Auto-join crew if user arrived via crew invite deep link
  useEffect(() => {
    const maybeJoin = async () => {
      if (!isAuthenticated) return;
      const code = localStorage.getItem('pendingCrewInviteCode');
      if (!code) return;
      localStorage.removeItem('pendingCrewInviteCode');
      try {
        const res = await crewService.joinCrew(code);
        if (res.success) {
          showToast({ title: 'Crew joined', message: `You joined "${res.crewName}". Open the Crew panel to see your crew.`, severity: 'success' });
        } else {
          showToast({ message: `Could not join crew: ${(res as any).error || 'Unknown error'}`, severity: 'error' });
        }
      } catch (err: any) {
        showToast({ message: `Could not join crew: ${err?.message || 'Unknown error'}`, severity: 'error' });
      }
    };
    maybeJoin();
  }, [isAuthenticated]);

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        backgroundColor: '#0f172a',
        flexDirection: 'column'
      }}>
        <style>
          {`
            @keyframes cyber-spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            @keyframes cyber-color {
              0% { border-top-color: #60a5fa; box-shadow: 0 0 20px #60a5fa40; }
              25% { border-top-color: #34d399; box-shadow: 0 0 20px #34d39940; }
              50% { border-top-color: #fbbf24; box-shadow: 0 0 20px #fbbf2440; }
              75% { border-top-color: #f472b6; box-shadow: 0 0 20px #f472b640; }
              100% { border-top-color: #60a5fa; box-shadow: 0 0 20px #60a5fa40; }
            }
            @keyframes cyber-pulse {
              0%, 100% { opacity: 0.6; transform: scale(1); }
              50% { opacity: 1; transform: scale(1.05); }
            }
          `}
        </style>
        <div style={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          border: '4px solid rgba(96, 165, 250, 0.2)',
          borderTopColor: '#60a5fa',
          animation: 'cyber-spin 1s linear infinite, cyber-color 3s ease-in-out infinite',
          marginBottom: 16
        }} />
        <p style={{
          color: '#94a3b8',
          fontSize: '1.1rem',
          fontWeight: 500,
          animation: 'cyber-pulse 2s ease-in-out infinite'
        }}>
          {t('appLoadingBrand')}
        </p>
      </div>
    );
  }

  // Always allow viewing shared JSAs without auth/subscription gates
  if (sharedJsaId) {
    return (
      <ErrorBoundary>
        <PublicJsaPdfViewer jsaId={sharedJsaId} onClose={() => setSharedJsaId(null)} />
      </ErrorBoundary>
    );
  }

  // Always allow viewing shared inspections without auth/subscription gates
  if (sharedInspectionId) {
    return (
      <ErrorBoundary>
        <PublicInspectionViewer inspectionId={sharedInspectionId} onClose={() => setSharedInspectionId(null)} />
      </ErrorBoundary>
    );
  }

  // Show Telegram Login if not authenticated via WebApp
  if (!isAuthenticated && !freeModeActive) {
    return (
      <ErrorBoundary>
        <Suspense fallback={<FullScreenLoader label={t('loading')} />}>
          <LandingPage onAuth={handleTelegramAuth} onFreeMode={handleStartFreeMode} />
        </Suspense>
      </ErrorBoundary>
    );
  }

  // Subscription check in flight — prevent fall-through to main app
  if (isAuthenticated && !freeModeActive && !subscriptionChecked) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100vh', backgroundColor: '#0f172a', flexDirection: 'column', gap: 16
      }}>
        <style>{`@keyframes cyber-spin{0%{transform:rotate(0deg)}100%{transform:rotate(360deg)}}`}</style>
        <div style={{
          width: 48, height: 48, borderRadius: '50%',
          border: '4px solid rgba(96, 165, 250, 0.2)',
          borderTopColor: '#06b6d4',
          animation: 'cyber-spin 1s linear infinite'
        }} />
        <p style={{ color: '#94a3b8', fontSize: '1rem', fontWeight: 500, margin: 0 }}>
          {t('verifyingSubscription')}
        </p>
      </div>
    );
  }

  const handleLinkPurchase = async () => {
    if (!linkEmail.trim()) return;
    setLinking(true);
    setLinkMsg(null);
    const result = await subscriptionService.linkPurchaseByEmail(linkEmail.trim());
    setLinking(false);
    if (result.success) {
      setLinkMsg({ type: 'success', text: t('purchaseLinkedLoading') });
      subscriptionService.clearCache();
      const checkResult = await runSubscriptionCheck('link-purchase');
      if (checkResult.allowed) {
        setSubscriptionAllowed(true);
      } else if (checkResult.verificationError) {
        setLinkMsg({ type: 'error', text: t('subscriptionVerifyFailed') });
      } else {
        // Subscription linked but status not yet reflecting — reload to pick up fresh state
        setLinkMsg({ type: 'success', text: t('purchaseLinkedRefreshing') });
        setTimeout(() => window.location.reload(), 1500);
      }
    } else {
      setLinkMsg({ type: 'error', text: result.error || t('purchaseNotFound') });
    }
  };

  const handleLinkTelegramPurchase = async () => {
    setLinkingTelegram(true);
    setLinkMsg(null);
    try {
      const result = await subscriptionService.linkPurchaseByTelegram();
      if (result.success) {
        setLinkMsg({ type: 'success', text: t('purchaseLinkedLoading') });
        subscriptionService.clearCache();
        setSubscriptionChecked(true);
        setSubscriptionAllowed(true);
        void runSubscriptionCheck('link-purchase');
      } else {
        setLinkMsg({ type: 'error', text: result.error || t('telegramPurchaseNotFound') });
      }
    } finally {
      setLinkingTelegram(false);
    }
  };

  if (!accessAllowed) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #0c1220 100%)',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: '24px', overflowY: 'auto'
      }}>
        <Typography variant="h4" sx={{ color: '#06b6d4', fontWeight: 800, mb: 0.5, textAlign: 'center' }}>
          ZiaWellNav
        </Typography>
        <Typography variant="body2" sx={{ color: '#94a3b8', mb: 4, textAlign: 'center' }}>
          {t('subscriptionGateSubtitle')}
        </Typography>
        <Button
          variant="contained"
          onClick={() => handleStartFreeMode()}
          sx={{
            mb: 2,
            px: 3,
            py: 1.1,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 900,
            background: 'linear-gradient(135deg, #22c55e, #06b6d4)',
            boxShadow: '0 0 22px rgba(34,197,94,0.32)',
          }}
        >
          {t('freeModeButtonDuration')}
        </Button>
        <Typography variant="caption" sx={{ color: '#64748b', mb: 2, textAlign: 'center', maxWidth: 420 }}>
          {t('freeModeDeviceNotice')}
        </Typography>
        <Alert
          severity="warning"
          sx={{
            mb: 2,
            maxWidth: 640,
            width: '100%',
            border: '1px solid rgba(245,158,11,0.32)',
            bgcolor: 'rgba(120,53,15,0.22)',
            color: '#fde68a',
            '& .MuiAlert-icon': { color: '#f59e0b' },
          }}
        >
          {t('freeModeTestingNotice')}
        </Alert>
        <Box
          sx={{
            mb: 2.25,
            p: 2.5,
            borderRadius: 2.5,
            background: 'linear-gradient(135deg, rgba(245,158,11,0.16), rgba(6,182,212,0.08))',
            border: '1px solid rgba(245,158,11,0.34)',
            maxWidth: 560,
            width: '100%',
            boxShadow: '0 0 28px rgba(245,158,11,0.14)',
          }}
        >
          <Typography variant="subtitle1" sx={{ color: '#fde68a', fontWeight: 950, mb: 0.5 }}>
            Full access with Telegram Stars
          </Typography>
          <Typography variant="body2" sx={{ color: '#cbd5e1', mb: 1.4 }}>
            If your company assigned you a business seat, access opens automatically. If not, buy a 30-day pass with Telegram Stars.
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
            <Chip
              size="small"
              label={starsPaywall?.monthly?.label || 'Pay 500 Stars for 30 days'}
              sx={{ bgcolor: 'rgba(245,158,11,0.16)', color: '#fde68a', border: '1px solid rgba(245,158,11,0.34)', fontWeight: 900 }}
            />
            <Chip
              size="small"
              label="Business seats bypass payment"
              sx={{ bgcolor: 'rgba(34,197,94,0.12)', color: '#bbf7d0', border: '1px solid rgba(34,197,94,0.28)', fontWeight: 900 }}
            />
          </Box>
          <Button
            variant="contained"
            fullWidth
            disabled={starsProcessing || checkingSubscription}
            onClick={() => void handleStarsAccessPayment('individual_monthly')}
            sx={{
              py: 1.25,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 950,
              background: 'linear-gradient(135deg, #f59e0b, #06b6d4)',
              color: '#0f172a',
              '&:hover': { background: 'linear-gradient(135deg, #fbbf24, #22d3ee)' },
            }}
          >
            {starsProcessing ? 'Opening Stars invoice...' : 'Pay 500 Stars for 30 days'}
          </Button>
          {starsMessage && (
            <Alert severity={starsMessage.type === 'error' ? 'error' : starsMessage.type === 'success' ? 'success' : 'info'} sx={{ mt: 1.5 }}>
              {starsMessage.text}
            </Alert>
          )}
        </Box>
        {checkingSubscription && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CircularProgress size={18} sx={{ color: '#06b6d4' }} />
            <Typography variant="caption" sx={{ color: '#94a3b8' }}>{t('subscriptionActivating')}</Typography>
          </Box>
        )}
        {subscriptionError && (
          <Alert severity="info" sx={{ mb: 2, maxWidth: 700, width: '100%' }}>{subscriptionError}</Alert>
        )}
        {subscriptionError && (
          <Button
            variant="outlined"
            disabled={checkingSubscription}
            onClick={() => { void runSubscriptionCheck('manual-retry'); }}
            sx={{ mb: 2, textTransform: 'none', fontWeight: 700, borderColor: 'rgba(6,182,212,0.45)', color: '#67e8f9' }}
          >
            {t('tryAgain')}
          </Button>
        )}
        {/* Already paid — prominent, shown first */}
        <Box sx={{ mb: 3, p: 2.5, borderRadius: 2, background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)', maxWidth: 480, width: '100%' }}>
          <Typography variant="subtitle2" sx={{ color: '#e2e8f0', mb: 0.5 }}>
            {t('subscriptionLinkTitle')}
          </Typography>
          <Typography variant="body2" sx={{ color: '#94a3b8', mb: 1.5 }}>
            {t('subscriptionLinkSubtitle')}
          </Typography>
          <Button
            fullWidth
            variant="contained"
            onClick={handleLinkTelegramPurchase}
            disabled={linkingTelegram || linking}
            sx={{ mb: 1.5, background: 'linear-gradient(135deg, #22c55e, #06b6d4)', whiteSpace: 'nowrap', fontWeight: 800, textTransform: 'none' }}
          >
            {linkingTelegram ? t('linkingPurchase') : t('linkTelegramPurchaseBtn')}
          </Button>
          <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 1 }}>
            {t('subscriptionEmailFallbackLabel')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder={t('purchaseEmailPlaceholder')}
              value={linkEmail}
              onChange={(e) => setLinkEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleLinkPurchase()}
              sx={{
                flex: 1, minWidth: 180,
                '& .MuiOutlinedInput-root': { color: '#e2e8f0', '& fieldset': { borderColor: 'rgba(59,130,246,0.4)' }, '&:hover fieldset': { borderColor: 'rgba(59,130,246,0.7)' } },
                '& input::placeholder': { color: '#64748b' },
              }}
            />
            <Button
              variant="contained"
              onClick={handleLinkPurchase}
              disabled={linking || !linkEmail.trim()}
              sx={{ background: 'linear-gradient(135deg, #3b82f6, #06b6d4)', whiteSpace: 'nowrap', fontWeight: 700 }}
            >
              {linking ? t('linkingPurchase') : t('linkPurchaseBtn')}
            </Button>
          </Box>
          {linkMsg && (
            <Typography variant="caption" sx={{ color: linkMsg.type === 'success' ? '#22c55e' : '#f87171', display: 'block', mt: 1 }}>
              {linkMsg.text}
            </Typography>
          )}
        </Box>
        {/* Pricing plans — collapsed by default so existing subscribers aren't confused */}
        <Typography variant="caption" sx={{ color: '#64748b', mb: 1, cursor: 'pointer', textDecoration: 'underline' }}
          onClick={() => setShowPricing(s => !s)}>
          {showPricing ? t('subscriptionHidePlans') : t('subscriptionShowPlans')}
        </Typography>
        {showPricing && (
          <Suspense fallback={<InlineLoader label={t('loading')} />}>
            <PricingSection />
          </Suspense>
        )}
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <HazardNotificationProvider enabled={true} pollInterval={30000}>
        <div className="min-h-screen bg-tg-bg">
          {freeModeActive && !subscriptionAllowed && (
            <Box
              title={`${runtimeSupportProfile.modeLabel} · ${runtimeSupportProfile.supportPercent}%`}
              sx={{
                position: 'fixed',
                top: { xs: 'auto', sm: FREE_MODE_COUNTDOWN_TOP_CSS },
                bottom: { xs: FREE_MODE_COUNTDOWN_MOBILE_BOTTOM_CSS, sm: 'auto' },
                left: { xs: FREE_MODE_COUNTDOWN_MOBILE_SIDE_GUTTER_CSS, sm: 'auto' },
                right: {
                  xs: FREE_MODE_COUNTDOWN_MOBILE_RIGHT_GUTTER_CSS,
                  sm: 'calc(env(safe-area-inset-right, 0px) + 8px)',
                },
                transform: 'none',
                zIndex: 20000,
                maxWidth: {
                  xs: 'calc(100vw - 24px)',
                  sm: 'calc(100vw - 24px)',
                },
                display: 'flex',
                justifyContent: 'center',
                px: { xs: 0.9, sm: 1.4 },
                py: { xs: 0.5, sm: 0.65 },
                borderRadius: '999px',
                border: '1px solid rgba(34,211,238,0.55)',
                bgcolor: 'rgba(2,6,23,0.82)',
                color: '#67e8f9',
                fontFamily: '"Orbitron", monospace',
                fontSize: { xs: '0.62rem', sm: '0.72rem' },
                fontWeight: 900,
                letterSpacing: { xs: '0.02em', sm: '0.05em' },
                textAlign: 'center',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                boxShadow: '0 0 18px rgba(34,211,238,0.35)',
                pointerEvents: 'none',
              }}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                {isBrowserFreeDemo
                  ? t('freeModeCountdownChromeDemoFull', freeModeMinutesRemaining)
                  : t('freeModeCountdownFull', freeModeMinutesRemaining)}
              </Box>
              <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                {isBrowserFreeDemo
                  ? t('freeModeCountdownChromeDemoShort', freeModeMinutesRemaining)
                  : t('freeModeCountdownShort', freeModeMinutesRemaining)}
              </Box>
            </Box>
          )}
          {isBrowserFreeDemo && freeModeDemoNoticeVisible && (
            <Box
              sx={{
                position: 'fixed',
                top: { xs: FREE_MODE_DEMO_NOTICE_IOS_TOP_CSS, sm: FREE_MODE_DEMO_NOTICE_TOP_CSS },
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 19999,
                width: 'min(92vw, 680px)',
              }}
            >
              <Alert
                severity="info"
                onClose={() => setFreeModeDemoNoticeVisible(false)}
                sx={{
                  bgcolor: 'rgba(2,6,23,0.9)',
                  color: '#bae6fd',
                  border: '1px solid rgba(34,211,238,0.35)',
                  boxShadow: '0 16px 32px rgba(2,6,23,0.35)',
                  '& .MuiAlert-icon': { color: '#67e8f9' },
                }}
              >
                {t('freeModeDemoEnteredNotice')}
              </Alert>
            </Box>
          )}
	          {subscriptionGraceNotice && (
	            <Box
	              sx={{
	                position: 'fixed',
	                top: 'calc(env(safe-area-inset-top, 0px) + 52px)',
	                left: '50%',
	                transform: 'translateX(-50%)',
	                zIndex: 20000,
	                width: 'min(92vw, 620px)',
	              }}
	            >
	              <Alert
	                severity="info"
	                onClose={() => setSubscriptionGraceNotice(null)}
	                sx={{
	                  bgcolor: 'rgba(2,6,23,0.9)',
	                  color: '#bae6fd',
	                  border: '1px solid rgba(34,211,238,0.45)',
	                  boxShadow: '0 0 18px rgba(34,211,238,0.25)',
	                  '& .MuiAlert-icon': { color: '#67e8f9' },
	                }}
	              >
	                {subscriptionGraceNotice}
	              </Alert>
	            </Box>
	          )}
          <Routes>
            <Route path="/race/*" element={
              <RaceRouteSplashGate onPreloadRaceApp={() => { void loadRaceApp(); }}>
                <Suspense fallback={<FullScreenLoader label="Loading Race Mode..." />}>
                  <RaceApp />
                </Suspense>
              </RaceRouteSplashGate>
            } />
            <Route path="*" element={
              <Suspense fallback={<FullScreenLoader label={t('loading')} />}>
                <VoiceNavigation />
              </Suspense>
            } />
          </Routes>
          <AppFeedbackHost />
        </div>
      </HazardNotificationProvider>
    </ErrorBoundary>
  );
}

export default App;
